import { useCallback, useEffect, useMemo, useState } from "react";
import { Download, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useApi, errMsg, downloadBlob } from "@/lib/api";
import { useMember } from "@/context/MemberContext";
import { euro, fmt, hoursNL, mondayOf, timeOf, toISO } from "@/lib/dates";
import { PageHeader, Panel, Loader } from "@/components/common";
import { WeekPicker } from "@/components/WeekPicker";
import { EntryDialog } from "@/components/EntryDialog";
import { AiHoursSummary } from "@/components/AiHoursSummary";

const TimesheetTable = ({ ts, isManager }) => (
  <Panel className="p-0 overflow-x-auto" data-testid="timesheet-table">
    <table className="w-full min-w-[760px] text-sm">
      <thead>
        <tr className="border-b border-slate-200 text-xs font-semibold uppercase tracking-wider text-slate-500">
          <th className="text-left p-3">Medewerker</th>
          {ts.days.map((d) => <th key={d} className="p-3 text-right">{fmt(d, "EEE d")}</th>)}
          <th className="p-3 text-right">Totaal</th>
          <th className="p-3 text-right">Gepland</th>
          {isManager && <th className="p-3 text-right">Bruto</th>}
        </tr>
      </thead>
      <tbody className="font-mono-num">
        {ts.rows.map((r) => (
          <tr key={r.user_id} className="border-b border-slate-100" data-testid={`timesheet-row-${r.user_id}`}>
            <td className="p-3 font-sans font-medium">{r.name}{r.open && <span className="ml-2 text-[11px] font-semibold text-emerald-600">● bezig</span>}</td>
            {r.hours.map((h, i) => <td key={i} className={`p-3 text-right ${h ? "text-slate-900" : "text-slate-300"}`}>{hoursNL(h)}</td>)}
            <td className="p-3 text-right font-bold" data-testid={`timesheet-total-${r.user_id}`}>{hoursNL(r.total)}</td>
            <td className="p-3 text-right text-slate-500">{hoursNL(r.planned)}</td>
            {isManager && <td className="p-3 text-right">{euro(r.gross)}</td>}
          </tr>
        ))}
        <tr className="bg-[#FAFAF7] font-bold">
          <td className="p-3 font-sans">Totaal</td>
          <td colSpan={7} />
          <td className="p-3 text-right" data-testid="timesheet-grand-total">{hoursNL(ts.total_hours)}</td>
          <td />
          {isManager && <td className="p-3 text-right">{euro(ts.total_gross)}</td>}
        </tr>
      </tbody>
    </table>
  </Panel>
);

export default function Timesheet() {
  const api = useApi();
  const { isManager } = useMember();
  const [monday, setMonday] = useState(mondayOf());
  const [ts, setTs] = useState(null);
  const [entries, setEntries] = useState([]);
  const [edit, setEdit] = useState(null);
  const names = useMemo(() => Object.fromEntries((ts?.rows || []).map((r) => [r.user_id, r.name])), [ts]);

  const load = useCallback(async () => {
    const params = { week_start: toISO(monday) };
    const [a, b] = await Promise.all([api.get("/timesheet", { params }), api.get("/time/entries", { params })]);
    setTs(a.data);
    setEntries(b.data);
  }, [api, monday]);

  useEffect(() => {
    load();
  }, [load]);

  const exportCsv = async () => {
    try {
      const res = await api.get("/timesheet/export", { params: { week_start: toISO(monday) }, responseType: "blob" });
      const name = res.headers["content-disposition"]?.match(/filename="(.+)"/)?.[1] || "urenoverzicht.csv";
      downloadBlob(res.data, name);
      toast.success("CSV gedownload");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="timesheet-page">
      <PageHeader title="Urenoverzicht" subtitle={isManager ? "Gewerkte uren per medewerker. Exporteer voor je boekhouder." : "Jouw gewerkte uren per week."}>
        <Button className="rounded-full bg-[#FF5A36] hover:bg-[#E04826]" onClick={exportCsv} data-testid="export-csv-button"><Download className="h-4 w-4 mr-2" />Exporteer CSV</Button>
      </PageHeader>
      <div className="mb-4"><WeekPicker monday={monday} onChange={setMonday} /></div>

      {!ts ? <Loader /> : (
        <>
          <TimesheetTable ts={ts} isManager={isManager} />
          {isManager && <AiHoursSummary weekStart={toISO(monday)} />}
          <Panel className="mt-4" data-testid="entries-panel">
            <h2 className="font-semibold text-lg mb-3">Registraties deze week</h2>
            {entries.length === 0 ? <p className="text-sm text-slate-500">Geen registraties in deze week.</p> : (
              <ul className="divide-y divide-slate-100">
                {entries.map((e) => (
                  <li key={e.id} className="flex items-center gap-3 py-2.5 text-sm" data-testid={`entry-row-${e.id}`}>
                    <span className="w-28 capitalize text-slate-600">{fmt(e.clock_in, "EEE d MMM")}</span>
                    {isManager && <span className="flex-1 font-medium truncate">{names[e.user_id] || "—"}</span>}
                    <span className={`font-mono-num ${isManager ? "" : "flex-1"}`}>{timeOf(e.clock_in)} – {e.clock_out ? timeOf(e.clock_out) : "nu"}</span>
                    <span className="w-16 text-right font-semibold font-mono-num">{e.clock_out ? hoursNL(e.hours) : "bezig"}</span>
                    {isManager && (
                      <Button size="icon" variant="ghost" onClick={() => setEdit(e)} data-testid={`entry-edit-${e.id}`} aria-label="Corrigeren"><Pencil className="h-4 w-4" /></Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </>
      )}
      <EntryDialog entry={edit} name={edit ? names[edit.user_id] : ""} onClose={() => setEdit(null)} onSaved={load} />
    </div>
  );
}
